<template>
  <v-dialog v-model="dialog" persistent width="1200px" class="messages-dialog">
    <v-card class="messages-card">
      <v-card-title class="messages-header" dark>
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-message-text</v-icon>
          <span class="text-h6 font-weight-medium white--text">Central de Mensagens</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn :disabled="is_loading" :loading="is_loading" dark icon
               @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="messages-content pa-4">
        <template v-if="chatLoading">
          <v-skeleton-loader
            type="article, actions"
            class="mx-auto"
          ></v-skeleton-loader>
          <div class="text-center pa-4">
            <v-progress-circular
              indeterminate
              color="primary"
              class="mb-2"
            ></v-progress-circular>
            <div class="text-subtitle-1 grey--text">Carregando mensagens...</div>
          </div>
        </template>
        <template v-else>
          <v-row class="new-message-section">
            <v-col cols="12">
              <v-textarea 
                v-model="message" 
                :disabled="is_loading_new_message"
                :loading="is_loading_new_message" 
                label="Nova Mensagem" 
                no-resize 
                outlined
                rows="3"
                class="message-textarea"
                hide-details
                placeholder="Digite sua mensagem aqui..."
              ></v-textarea>
            </v-col>
            <v-col cols="12" class="d-flex align-center">
              <div class="flex-grow-1">
                <slot name="below-message-input"></slot>
              </div>
              <v-btn 
                :disabled="is_loading_new_message || !message" 
                color="primary" 
                @click="addMessage()"
                class="send-button"
                elevation="2"
              >
                <v-icon left>mdi-send</v-icon>
                Enviar
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="messages-list">
            <v-row>
              <v-col v-for="(messageLoop, index) in messages" :key="messageLoop.id_doc" cols="12">
                <v-card class="message-card" elevation="1">
                  <v-card-text class="pa-4">
                    <div class="message-header d-flex align-center">
                      <div class="d-flex align-center flex-grow-1">
                        <span class="chip-user" v-bind:style="{ backgroundColor: getColor(messageLoop.user_rank) }">
                          <v-icon v-if="!messageLoop.image_url" color="white">
                            mdi-account
                          </v-icon>
                          <span v-else class="span-img-user">
                            <img :src="messageLoop.image_url" alt="img_user" class="img-user"/>
                          </span>
                        </span>
                        <div class="sender-info ml-3">
                          <div class="d-flex align-center">
                            <span class="sender-name font-weight-medium">{{ messageLoop.sender_name }}</span>
                            <v-chip
                              small
                              class="ml-2"
                              color="primary"
                              text-color="white"
                              elevation="1"
                            >
                              {{ getTagDisplayName(messageLoop.tag) }}
                            </v-chip>
                          </div>
                          <div class="message-meta">
                            <span class="message-time">{{ formatDateOnMessages(messageLoop.created_at) }}</span>
                            <span v-if="messageLoop.updated_at" class="message-edited ml-2">
                              <v-icon small color="grey">mdi-pencil</v-icon>
                              Editado em {{ messageLoop.formatted_updated_at }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="message-actions d-flex align-center">
                        <v-btn
                            :disabled="is_loading_message"
                            color="primary"
                            text
                            @click="toggleResponse(index)"
                            class="response-button mr-2">
                          <v-icon small class="mr-1">{{ responseTextArea[index] ? 'mdi-close' : 'mdi-reply' }}</v-icon>
                          <span>{{ responseTextArea[index] ? 'Cancelar' : 'Responder' }}</span>
                        </v-btn>
                        <v-btn v-if="getOptions(messageLoop).length" 
                               :disabled="is_loading_message"
                               text 
                               @click="toggleDropdownMessage(index)"
                               class="action-button">
                          <v-icon color="primary">mdi-dots-vertical</v-icon>
                        </v-btn>
                        <div v-if="showDropdownMessage[index]" class="dropdown-message">
                          <v-list dense>
                            <v-list-item
                                v-for="(item, idx) in getOptions(messageLoop)"
                                :key="idx"
                                @click="handleClick(item, index)"
                                class="dropdown-item">
                              <v-icon :color="item.color" class="me-3" small>
                                {{ item.icon }}
                              </v-icon>
                              <v-list-item-title class="text-body-2">{{ item.text }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </div>
                      </div>
                    </div>

                    <div class="message-content mt-3">
                      <span v-if="!isEditingMessage(index)" class="message-text">
                        {{ messageLoop.message }}
                      </span>
                      <v-textarea
                          v-else
                          v-model="editedMessage"
                          :disabled="is_loading_message"
                          :loading="is_loading_message"
                          append-icon="mdi-send"
                          label="Editar Mensagem"
                          no-resize
                          outlined
                          prepend-icon="mdi-close"
                          rows="3"
                          @click:prepend="cleanStates"
                          @click:append="handleEditSave(index, '', 'message')"
                          class="edit-textarea"
                      ></v-textarea>
                    </div>

                    <v-expand-transition>
                      <div v-show="responseTextArea[index]" class="response-section mt-3">
                        <v-textarea
                            v-model="message_response"
                            :disabled="is_loading_message"
                            :loading="is_loading_message"
                            append-icon="mdi-send"
                            label="Responder Mensagem"
                            no-resize
                            outlined
                            rows="3"
                            @click:append="handleSendResponse(index)"
                            class="response-textarea"
                        ></v-textarea>
                      </div>
                    </v-expand-transition>

                    <v-expand-transition>
                      <div v-if="(messageLoop.comments && messageLoop.comments.length)" class="comments-section mt-4">
                        <v-divider class="mb-4"></v-divider>
                        <div v-for="(comment, commentIndex) in messageLoop.comments"
                             :key="comment.id_doc"
                             class="comment-card mb-4">
                          <div class="comment-header d-flex align-center">
                            <span class="chip-user" v-bind:style="{ backgroundColor: getColor(comment.user_rank) }">
                              <v-icon v-if="!comment.image_url" color="white" small>
                                mdi-account
                              </v-icon>
                              <span v-else class="span-img-user">
                                <img :src="comment.image_url" alt="img_user" class="img-user"/>
                              </span>
                            </span>
                            <div class="sender-info ml-3">
                              <div class="d-flex align-center">
                                <span class="sender-name font-weight-medium">{{ comment.sender_name }}</span>
                                <v-chip
                                  small
                                  class="ml-2"
                                  color="primary"
                                  text-color="white"
                                  elevation="1"
                                >
                                  {{ getTagDisplayName(comment.tag) }}
                                </v-chip>
                              </div>
                              <div class="message-meta">
                                <span class="message-time">{{ formatDateOnMessages(comment.created_at, 'DD/MM/YYYY HH:mm') }}</span>
                                <span v-if="comment.updated_at" class="message-edited ml-2">
                                  <v-icon small color="grey">mdi-pencil</v-icon>
                                  Editado em {{ comment.formatted_updated_at }}
                                </span>
                              </div>
                            </div>
                            <v-spacer></v-spacer>
                            <div class="comment-actions">
                              <v-btn v-if="getOptions(comment).length" 
                                     :disabled="is_loading_message"
                                     text 
                                     @click="toggleDropdownComment(index, commentIndex)"
                                     class="action-button">
                                <v-icon color="primary" small>mdi-dots-vertical</v-icon>
                              </v-btn>
                              <div v-if="showDropdownComment[index] && showDropdownComment[index][commentIndex]" class="dropdown-message">
                                <v-list dense>
                                  <v-list-item
                                      v-for="(item, idx) in getOptions(comment)"
                                      :key="idx"
                                      @click="handleCommentClick(item, index, commentIndex)"
                                      class="dropdown-item">
                                    <v-icon :color="item.color" class="me-3" small>
                                      {{ item.icon }}
                                    </v-icon>
                                    <v-list-item-title class="text-body-2">{{ item.text }}</v-list-item-title>
                                  </v-list-item>
                                </v-list>
                              </div>
                            </div>
                          </div>
                          <div class="comment-content mt-3">
                            <span v-if="!isEditingComment(index, commentIndex)" class="comment-text">
                              {{ comment.comment }}
                            </span>
                            <v-textarea
                                v-else
                                v-model="editedComment"
                                :disabled="is_loading_message"
                                :loading="is_loading_message"
                                append-icon="mdi-send"
                                label="Editar Comentário"
                                no-resize
                                outlined
                                prepend-icon="mdi-close"
                                rows="3"
                                @click:prepend="cleanStates"
                                @click:append="handleEditSave(index, commentIndex, 'comment')"
                                class="edit-textarea"
                            ></v-textarea>
                          </div>
                        </div>
                      </div>
                    </v-expand-transition>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>
          <InfiniteLoading @infinite="infiniteHandler">
            <div slot="no-more" class="text-center pa-4 grey--text">Todos os dados carregados.</div>
            <div slot="no-results" class="text-center pa-4 grey--text">Todos os dados carregados.</div>
          </InfiniteLoading>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { showSuccessSnackBar, showErrorSnackBar } from "@/utils/SnackBartHelper";
import { showDeleteDialogAlert } from "@/utils/DialogAlertHelper";
import Utils from '@/scripts/Utils';
import ProjectMessagesService from "@/services/firebase/ProjectsMessages/ProjectMessagesService.js";
import UsersServiceFirebase from '@/services/firebase/Users/UsersService';
import InfiniteLoading from 'vue-infinite-loading';
import ProjectsService from "@/services/server/ProjectsService.js";

export default {
  name: "ProjectMessagesDialog",

  components: {
    InfiniteLoading,
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    currentStep: {
      type: String,
      default: null
    }
  },

  data: () => ({
    dialog: false,
    is_loading: false,
    showDropdownMessage: {},
    showDropdownComment: {},
    messages: [],
    responseTextArea: [],
    editingStates: {},
    editedMessage: null,
    editedComment: null,
    is_loading_modal_messages: {},
    is_loading_message: false,
    is_loading_new_message: false,
    message: null,
    message_response: null,
    chatLoading: false,
    lastDocSnapshot: null,
    resetInfiniteHandler: true,
    selectedTag: null,
    stepTagMap: {
      'prospecting': 'prospecting',
      'diagnostic': 'diagnostic',
      'approval': 'approval',
      'closing': 'closing',
      'contract': 'contract',
      'contracts': 'contract',
      'delivery': 'delivery',
      'waterfix': 'waterfix'
    },
    availableTags: [
      { value: 'prospecting', displayName: 'Prospecção' },
      { value: 'diagnostic', displayName: 'Waterscam' },
      { value: 'approval', displayName: 'Aprovação' },
      { value: 'closing', displayName: 'Fechamento' },
      { value: 'contract', displayName: 'Contrato' },
      { value: 'delivery', displayName: 'Entrega' },
      { value: 'waterfix', displayName: 'Instalação' }
    ],
    rankMapper: [
      { text: "Super Admin", value: 0 },
      { text: "Admin", value: 1 },
      { text: "Franqueado", value: 2 },
      { text: "Cliente", value: 4 },
      { text: "Associado", value: 5 }
    ],
  }),

  computed: {
    ...mapGetters([
      "getCurrentUserData",
      "getCurrentUserRank",
    ]),
  },

  methods: {
    async infiniteHandler($state) {
      if (this.resetInfiniteHandler) {
        this.resetInfiniteHandler = false;
        $state.reset();
        return $state.loaded();
      }

      if (!this.lastDocSnapshot) {
        return $state.complete();
      }

      try {
        this.is_loading_modal_messages = { action: 'load' };
        const currentTag = this.currentStep ? this.stepTagMap[this.currentStep] : null;
        const { result, messages, lastDocSnapshot } = await ProjectMessagesService.paginationCallsMessagesByProject(
          this.lastDocSnapshot, 
          this.projectId,
          currentTag
        );
        this.lastDocSnapshot = lastDocSnapshot;
        
        if (messages && messages.length > 0) {
          this.messages.push(...messages);
          await this.addMessagesToArray();
          return $state.loaded();
        }
        
        return $state.complete();
      } catch (error) {
        console.error("Erro ao carregar mais mensagens:", error);
        showErrorSnackBar("Erro ao carregar mais mensagens");
        return $state.error();
      } finally {
        this.is_loading_modal_messages = false;
      }
    },

    async addMessagesToArray(){
      const senders_id = new Set();
      
      this.messages.map(message => {
        senders_id.add(message.sender_id)
        
        if (message.comments && Array.isArray(message.comments)) {
          message.comments.forEach(comment => {
            senders_id.add(comment.sender_id)
          });
        }
      });
      
      const users_data = await UsersServiceFirebase.getUsersByIdList(Array.from(senders_id))
      
      this.messages = this.messages.map(message => {
        const user_message = users_data.find(user => user.id === message.sender_id);
        
        if (message.updated_at) {
          message = {...message,
            formatted_updated_at: this.formatDateOnMessages(message.updated_at)};
          }
          
        message = {
          ...message,
          user_rank: this.getUserRank(user_message?.rank),
          image_url: user_message?.metadata?.imageURL
        }
        
        if (message.comments && Array.isArray(message.comments)) {
          message.comments = message.comments.map(comment => {
            const user_comment = users_data.find(user => user.id === comment.sender_id);
            
            if (comment.updated_at) {
              comment = {
                ...comment,
                formatted_updated_at: this.formatDateOnMessages(comment.updated_at)
              };
            }
            comment = {
              ...comment,
              user_rank: this.getUserRank(user_comment.rank),
              image_url: user_comment.metadata.imageURL
            }
            return comment;
          });
        }
        
        return message;
      })

      this.initAndResetDropdowns();
      this.is_loading_modal_messages = false;

      // Só atualiza os readers quando não houver currentStep (visualização geral)
      if (!this.currentStep && this.messages.length) {
        await this.insertReaderOnProject();
      }
    },

    getOptions(item) {
      const options = [];

      const id_user = this.getCurrentUserData.id
      const user_rank = this.getCurrentUserRank

      const canEdit = item.sender_id === id_user;
      const canDelete =
        ((user_rank === 'Admin' || user_rank === 'Super Admin') || item.sender_id === id_user) ||
        (user_rank === 'Franqueado' && item.sender_id === id_user);

      if (canEdit) {
        options.push({
          text: 'Editar',
          icon: 'mdi-pencil',
          color: 'primary'
        });
      }
      if (canDelete) {
        options.push({
          text: 'Deletar',
          icon: 'mdi-delete',
          color: 'error'
        });
      }

      return options;
    },

    loadingAction(actionType) {
      return this.is_loading_modal_messages && this.is_loading_modal_messages.action === actionType
    },

    initAndResetDropdowns() {
      this.responseTextArea = Array(this.messages.length).fill(false);
      this.showDropdownMessage = Array(this.messages.length).fill(false);
      this.showDropdownComment = this.messages.map(message => {
        return message.comments ?
        Array(message.comments.length).fill(false) :
        [];
      });
    },

    getColor(userRank) {
      const rankColors = {
        "Super Admin": '#182B57',
        "Admin" : '#182B57',
        "Franqueado": '#1976D2',
        "Cliente": '#1976D2',
        "Associado": '#1976D2'
      };
      return rankColors[userRank] || '#808080'; // Cor padrão se não encontrado
    },

    getUserRank(rank) {
      try {
        return this.rankMapper.find(m => m.value === rank)?.text || 'Desconhecido';
      } catch (error) {
        console.error("Erro ao buscar rank do usuário:", error);
        return 'Desconhecido';
      }
    },

    formatDateOnMessages(date) {
      if (date['seconds'] !== undefined && date['nanoseconds'] !== undefined) {
        return Utils.formatDateDayJs(date['seconds'] * 1000, 'DD/MM/YYYY [às] HH:mm');
      } else {
        return Utils.formatDateDayJs(new Date(date), 'DD/MM/YYYY [às] HH:mm');
      }
    },

    toggleDropdownComment(messageIndex, commentIndex) {
      if (this.showDropdownComment[messageIndex]?.[commentIndex]) {
        this.$set(this.showDropdownComment[messageIndex], commentIndex, false);
      } else {
        this.handleCloseAll();
        this.$set(this.showDropdownComment[messageIndex], commentIndex, true);
      }
    },

    toggleDropdownMessage(messageIndex) {
      const isOpen = this.showDropdownMessage[messageIndex];
      if (isOpen) {
        this.$set(this.showDropdownMessage, messageIndex, false);
      } else {
        this.handleCloseAll();
        this.$set(this.showDropdownMessage, messageIndex, true);
      }
    },

    handleCloseAll() {
      this.showDropdownMessage = Array(this.messages.length).fill(false);
      this.showDropdownComment = Array(this.messages.length).fill().map(() => []);
    },

    async addMessage(){
      if (this.message && this.message.trim()) {
        const currentTag = this.currentStep ? (this.stepTagMap[this.currentStep] || 'general') : 'general';
        
        let messageTemplate = {
          created_at: new Date(),
          sender_name: this.getCurrentUserData.name,
          sender_id: this.getCurrentUserData.id,
          message: this.message.trim(),
          id_project: this.projectId,
          readers: [{reader_id: this.getCurrentUserData.id, read_at: new Date()}],
          tag: currentTag
        }

        this.is_loading_new_message = true;
        messageTemplate.id_doc = await ProjectMessagesService.createMessage(messageTemplate);
        const user_data = await UsersServiceFirebase.getUsersById(messageTemplate.sender_id);
        await this.clearReadersOnProject();
        messageTemplate.comments = [];
        messageTemplate.image_url = user_data?.metadata?.imageURL;
        messageTemplate.user_rank = this.getUserRank(user_data?.rank);

        this.messages.unshift(messageTemplate);
        this.handleCloseAll();
        this.cleanStates();

        if (messageTemplate.id_doc) {
          this.$emit('contract-change-requested', messageTemplate.id_doc);
        }
        
        this.message = null;
        this.is_loading_new_message = false;
        showSuccessSnackBar("Mensagem adicionada com sucesso!")
      }
    },

    getTagDisplayName(tagValue) {
      const tag = this.availableTags.find(t => t.value === tagValue);
      return tag ? tag.displayName : 'GERAL';
    },

    toggleResponse(index) {
      this.message_response = null
      const actualStatus = this.responseTextArea[index]
      this.showDropdownMessage = Array(this.messages.length).fill(false);
      this.showDropdownComment = Array(this.messages.length).fill().map(() => Array().fill(false));
      this.responseTextArea = Array(this.messages.length).fill(false);
      this.$set(this.responseTextArea, index, !actualStatus)
    },

    async handleSendResponse(index) {
      if (!this.message_response) {
        return;
      }

      if (this.messages[index]) {
        this.is_loading_message = true;
        let responseTemplate = {
          sender_id: this.getCurrentUserData.id,
          sender_name: this.getCurrentUserData.name,
          comment: this.message_response.trim(),
          created_at: new Date(),
          id_message: this.messages[index].id_doc,
          readers: [{reader_id: this.getCurrentUserData.id, read_at: new Date()}],
          tag: this.messages[index].tag
        }

        responseTemplate.id_doc = await ProjectMessagesService.createComment(responseTemplate);
        const user_data = await UsersServiceFirebase.getUsersById(responseTemplate.sender_id);
        await this.clearReadersOnProject();
        responseTemplate.image_url = user_data?.metadata?.imageURL;
        responseTemplate.user_rank = this.getUserRank(user_data?.rank);

        this.messages[index].comments = this.messages[index].comments || [];
        this.messages[index].comments.unshift(responseTemplate);

        showSuccessSnackBar("Mensagem adicionada com sucesso!")
      }

      this.$set(this.responseTextArea, index, false)
      this.message_response = null
      this.is_loading_message = false;
    },

    cleanStates() {
      this.editingStates = {};
    },

    isEditingMessage(messageIndex) {
      return this.editingStates[messageIndex] === 'message';
    },

    isEditingComment(messageIndex, commentIndex) {
      return this.editingStates[`${messageIndex}-${commentIndex}`] === 'comment';
    },

    toggleEditMode(messageIndex, commentIndex = null, type = 'message') {
      this.cleanStates();
      const key = type === 'message' ? messageIndex : `${messageIndex}-${commentIndex}`;

      if (this.editingStates[key]) {
        this.editingStates[key] = null;
      } else if (type === 'message') {
        this.editingStates[key] = type;
        this.editedMessage = this.messages[messageIndex].message;
      } else {
        this.editingStates[key] = type;
        this.editedComment = this.messages[messageIndex].comments[commentIndex].comment;
      }
    },

    async handleClick(item, index) {
      const message = this.messages[index];
      this.$set(this.showDropdownMessage, index, false);

      if (item.text === 'Deletar') {
        showDeleteDialogAlert("Deseja deletar essa mensagem? essa ação é irreversível!", async () => {
          await ProjectMessagesService.deleteMessage(message.id_doc);
          this.messages = this.messages.filter(msg => msg.id_doc !== message.id_doc);
          showSuccessSnackBar("Mensagem deletada com sucesso!")
        })
      } else if (item.text === 'Editar') {
        this.toggleEditMode(index)
      }
    },

    handleCommentClick(item, messageIndex, commentIndex) {
      const message = this.messages[messageIndex];
      const comment = message.comments[commentIndex];

      this.$set(this.showDropdownComment[messageIndex], commentIndex, false);

      if (item.text === 'Deletar') {
        showDeleteDialogAlert("Deseja deletar esse comentário? essa ação é irreversível!", async () => {
          const result = await ProjectMessagesService.deleteComment(message.id_doc, comment.id_doc);
          if (result) {
            message.comments = message.comments.filter(msg => msg.id_doc !== comment.id_doc);
            showSuccessSnackBar("Mensagem deletada com sucesso!")
          }
        })
      } else if (item.text === 'Editar') {
        this.toggleEditMode(messageIndex, commentIndex, 'comment');
      }
    },

    async handleEditSave(messageIndex, commentIndex, type = 'message') {
      let message = this.messages[messageIndex];
      this.is_loading_message = true;
      if (type === 'message') {
        const [status, responseSave] = await ProjectMessagesService.updateMessage(message.id_doc, this.editedMessage.trim());
        if (status) {
          await this.clearReadersOnProject();
          this.messages[messageIndex] = {
            ...responseSave,
            ...message,
            message: this.editedMessage,
            formatted_updated_at: this.formatDateOnMessages(responseSave.updated_at)
          }
          showSuccessSnackBar("Mensagem editada com sucesso!")
        }
      } else if (type === 'comment') {
        const comment = message.comments[commentIndex];
        const [status, responseSave] = await ProjectMessagesService.updateComment(message.id_doc, comment.id_doc, this.editedComment.trim());
        if (status) {
          await this.clearReadersOnProject();
          message.comments[commentIndex] = {
            ...responseSave,
            ...comment,
            comment: this.editedComment,
            formatted_updated_at: this.formatDateOnMessages(responseSave.updated_at)
          }
          showSuccessSnackBar("Mensagem editada com sucesso!")
        }
      }
      this.is_loading_message = false;
      this.cleanStates();
    },

    async insertReaderOnProject(){
      const project = await ProjectsService.getProjectById(this.projectId);
      if (!project.readers?.some(reader => reader.reader_id === this.getCurrentUserData.id))  {
        const project_readers = project.readers || [];
        const reader = {
          reader_id: this.getCurrentUserData.id,
          read_at: new Date()
        }

        project_readers.push(reader)

        await ProjectsService.updateProject(this.projectId, {
          readers: project_readers
        })
      }
    },

    async clearReadersOnProject(){
      const readers = [{
        reader_id: this.getCurrentUserData.id,
        read_at: new Date()
      }]

      await ProjectsService.updateProject(this.projectId, {
        readers: readers
      })
    },

    closeDialog() {
      this.dialog = false;
      this.message = null;
      this.selectedTag = null;
      this.$emit('input', false);
      this.$emit('dialog-closed', this.projectId);
    },

    async openDialog() {
      this.dialog = true;
      this.resetInfiniteHandler = true;
      this.lastDocSnapshot = null;
      this.messages = [];
      this.chatLoading = true;
      this.$emit('input', true);
      try {
        const currentTag = this.currentStep ? this.stepTagMap[this.currentStep] : null;
        
        const { result, messages, lastDocSnapshot } = await ProjectMessagesService.paginationCallsMessagesByProject(
          null, 
          this.projectId,
          currentTag
        );

        this.lastDocSnapshot = lastDocSnapshot;
        this.messages = messages;
        await this.addMessagesToArray();

        // Só atualiza os readers quando não houver currentStep (visualização geral)
        if (!this.currentStep) {
          await this.clearReadersOnProject();
        }
      } catch (error) {
        console.error("Erro ao carregar mensagens:", error);
        showErrorSnackBar("Erro ao carregar mensagens");
      } finally {
        this.chatLoading = false;
      }
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.dialog = newVal;
      }
    },
    projectId: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.dialog) {
          this.openDialog();
        }
      }
    },
    loading: {
      immediate: true,
      handler(newVal) {
        this.is_loading = newVal;
      }
    }
  },

  async created() {
    if (this.projectId && this.dialog) {
      this.openDialog();
    }
  }
};
</script>

<style scoped>
.chip-user {
  border: none;
  padding: 5px;
  border-radius: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;
  background-color: #000;
  margin-top: 10px;
}

.chip-user .v-icon {
  font-size: 28px;
}

.span-img-user {
  height: 100%;
  width: 100%;
  border-radius: 50%;
  overflow: hidden;
}

.img-user {
  width: 100%;
  height: 100%;
  object-fit: fill;
  display: block;
}

.sender-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 200px;
}

.sender-name {
  font-size: 1rem;
  color: #2c3e50;
  margin-bottom: 4px;
  line-height: 1.2;
}

.message-meta {
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.2;
}

.message-time {
  color: #666;
}

.message-edited {
  color: #666;
  font-size: 0.85rem;
}

.message-content {
  color: #2c3e50;
  line-height: 1.5;
}

.message-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.action-button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-button:hover {
  opacity: 1;
}

.dropdown-message {
  position: absolute;
  right: 0;
  top: 100%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 1000;
  min-width: 180px;
  margin-top: 4px;
}

.dropdown-item {
  transition: background-color 0.2s ease;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.response-button {
  text-transform: none;
  font-weight: 500;
  min-width: auto;
}

.response-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.comments-section {
  margin-left: 16px;
  border-left: 2px solid #e0e0e0;
  padding-left: 16px;
}

.comment-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.comment-header {
  margin-bottom: 8px;
}

.comment-content {
  color: #2c3e50;
  line-height: 1.5;
}

.comment-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.edit-textarea {
  border-radius: 8px;
}

/* Scrollbar styling */
.messages-content::-webkit-scrollbar {
  width: 8px;
}

.messages-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.messages-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.messages-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.message-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.comment-actions {
  position: relative;
}

.messages-dialog {
  border-radius: 12px;
}

.messages-card {
  border-radius: 12px;
  overflow: hidden;
}

.messages-header {
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  padding: 16px 24px;
}

.messages-content {
  background-color: #f5f5f5;
  min-height: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.new-message-section {
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.message-textarea {
  border-radius: 8px;
}

.send-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.message-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.message-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
}

.message-header {
  position: relative;
}
</style> 